/**
 * Analytics schemas
 */
import { z } from "zod";
import { NestedPaginationParamsSchema, SeveritySchema, SortParamsSchema, StrapiIdSchema } from "./common.schemas";
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Warning type schema - alias for SeveritySchema for backwards compatibility
 */
export const WarningTypeSchema = SeveritySchema;
/**
 * Target users schema
 */
export const TargetUsersSchema = z.enum(["all", "vip", "free", "specific"]);
/**
 * Admin stats query params schema
 */
export const AdminStatsQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z.record(z.unknown()).optional(),
    cached: z.boolean().optional(),
})
    .strict();
/**
 * Warning query params schema
 */
export const WarningQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        type: z.object({ $eq: z.string().optional() }).strict().optional(),
        isActive: z.object({ $eq: z.boolean().optional() }).strict().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Warning input schema
 */
export const WarningInputSchema = z
    .object({
    title: z.string().min(1, "Title is required"),
    message: z.string().min(1, "Message is required"),
    type: WarningTypeSchema,
    isActive: z.boolean().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    targetUsers: TargetUsersSchema.optional(),
    targetUserIds: z.array(StrapiIdSchema).optional(),
})
    .strict();
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Month charges data schema - charges for a single month
 */
export const MonthChargesSchema = z.object({
    charged: z.number(),
    pending: z.number().optional(),
    /**
     * Completed charges in this month up to the same day-of-month as today.
     * Lets the current (partial) month be compared against a like-for-like slice
     * of an earlier month instead of against that month's full total.
     */
    chargedThroughDay: z.number().optional(),
});
/**
 * Per month charges record - keyed by month number (1-12)
 */
export const PerMonthChargesSchema = z.record(z.string(), MonthChargesSchema);
/**
 * User charges data schema - charges data for a single user
 */
export const UserChargesDataSchema = z.object({
    id: StrapiIdSchema,
    email: z.string(),
    name: z.string().nullish(),
    isVIP: z.boolean().nullish(),
    blocked: z.boolean().optional(),
    role: z.object({ name: z.string() }).optional(),
    perMonth: PerMonthChargesSchema,
    totalCharged: z.number(),
    totalPending: z.number(),
});
/**
 * Total charges data schema - aggregated charges data
 */
export const TotalChargesDataSchema = z.object({
    perMonth: PerMonthChargesSchema,
    totalCharged: z.number(),
    totalPending: z.number(),
});
/**
 * Monthly charges data schema - response from getYearMonthlyCharges
 */
export const MonthlyChargesDataSchema = z.object({
    perUser: z.array(UserChargesDataSchema),
    total: TotalChargesDataSchema,
});
/**
 * Daily signups day data schema - stats for a single day
 */
export const DailySignupsDayDataSchema = z.object({
    total: z.number(),
    notVerified: z.number(),
    nonClients: z.number(),
    clients: z.number(),
    users: z.array(z.unknown()).optional(),
});
/**
 * Daily signups data schema - per day breakdown keyed by day number
 */
export const DailySignupsDataSchema = z.object({
    total: z.number(),
    perDay: z.record(z.string(), DailySignupsDayDataSchema),
});
/**
 * Yearly sign-ups month data schema - sign-ups for a single month
 */
export const YearlySignupsMonthDataSchema = z.object({
    total: z.number(),
    notVerified: z.number(),
    nonClients: z.number(),
    clients: z.number(),
});
/**
 * Yearly sign-ups data schema - per month breakdown keyed by month number (1-12)
 */
export const YearlySignupsDataSchema = z.object({
    perMonth: z.record(z.string(), YearlySignupsMonthDataSchema),
});
/**
 * Users per country item schema - stats for a single country
 */
export const UsersPerCountryItemSchema = z.object({
    total: z.number(),
    notVerified: z.number(),
    nonClients: z.number(),
    clients: z.number(),
});
/**
 * Users per country schema - keyed by country code (e.g., "ES", "NL")
 */
export const UsersPerCountrySchema = z.object({
    total: z.number(),
    perCountry: z.record(z.string(), UsersPerCountryItemSchema),
});
/**
 * Bots per country item schema
 */
export const BotsPerCountryItemSchema = z.object({
    country: z.string(),
    countryCode: z.string(),
    count: z.number(),
    activeCount: z.number(),
    percentage: z.number(),
});
/**
 * Bots per country schema
 */
export const BotsPerCountrySchema = z.object({
    countries: z.array(BotsPerCountryItemSchema),
    total: z.number(),
    activeTotal: z.number(),
});
/**
 * Admin referrer stats schema
 */
export const AdminReferrerStatsSchema = z.object({
    userId: StrapiIdSchema,
    email: z.string(),
    name: z.string().optional(),
    totalReferrals: z.number(),
    activeReferrals: z.number(),
    totalEarnings: z.number(),
    totalPayouts: z.number(),
    pendingBalance: z.number(),
    conversionRate: z.number(),
    averageOrderValue: z.number(),
    lifetimeValue: z.number(),
});
/**
 * Stats per referrer schema
 */
export const StatsPerReferrerSchema = z.object({
    referrers: z.array(AdminReferrerStatsSchema),
    totals: z.object({
        totalReferrals: z.number(),
        activeReferrals: z.number(),
        totalEarnings: z.number(),
        totalPayouts: z.number(),
    }),
});
/**
 * Monthly bot stats schema
 */
export const MonthlyBotStatsSchema = z.object({
    botConfigId: StrapiIdSchema,
    month: z.string(),
    profit: z.number(),
    trades: z.number(),
    winRate: z.number(),
    drawdown: z.number(),
    balance: z.number(),
    equity: z.number(),
    deposits: z.number(),
    withdrawals: z.number(),
});
/**
 * Monthly full stats per bot schema
 */
export const MonthlyFullStatsPerBotSchema = z.object({
    bots: z.array(MonthlyBotStatsSchema),
    month: z.string(),
    totals: z.object({
        totalProfit: z.number(),
        totalTrades: z.number(),
        averageWinRate: z.number(),
        maxDrawdown: z.number(),
    }),
});
/**
 * Currency amount map schema (e.g., { EUR: 100, USD: 50 })
 */
const PayoutCurrencyTotalSchema = z.record(z.string(), z.number());
/**
 * Payout user schema (simplified user info in payout)
 */
const PayoutUserSchema = z.object({
    id: StrapiIdSchema,
    name: z.string().optional(),
    email: z.string(),
});
/**
 * Payout item schema - individual payout in the monthly breakdown
 */
const PayoutItemSchema = z.object({
    id: StrapiIdSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    pm_paymentProcessor: z.string().optional(),
    pm_processor_payoutId: z.string().optional(),
    pm_status: z.string(),
    pm_currency: z.string(),
    pm_amount: z.number(),
    pm_reason: z.string().optional(),
    pm_invoiceNumber: z.string().optional(),
    pm_user: PayoutUserSchema.optional(),
});
/**
 * Monthly payout data schema - data for a single month
 */
const MonthlyPayoutDataSchema = z.object({
    total: PayoutCurrencyTotalSchema,
    payouts: z.array(PayoutItemSchema),
});
/**
 * Year payout data schema - data for a single year
 */
const YearPayoutDataSchema = z.object({
    perMonth: z.record(z.string(), MonthlyPayoutDataSchema),
    total: PayoutCurrencyTotalSchema,
});
/**
 * Yearly monthly payouts schema - Record keyed by year string ("2023", "2024", etc.)
 * Each year contains perMonth breakdown and total
 */
export const YearlyMonthlyPayoutsSchema = z.record(z.string(), YearPayoutDataSchema);
/**
 * Warning schema
 */
export const WarningSchema = z.object({
    id: StrapiIdSchema,
    title: z.string(),
    message: z.string(),
    type: WarningTypeSchema,
    isActive: z.boolean(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    targetUsers: TargetUsersSchema.optional(),
    targetUserIds: z.array(StrapiIdSchema).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * News schema
 */
export const NewsSchema = z.object({
    id: StrapiIdSchema,
    title: z.string(),
    content: z.string(),
    summary: z.string().optional(),
    imageUrl: z.string().optional(),
    publishedAt: z.string(),
    slug: z.string().optional(),
    tags: z.array(z.string()).optional(),
});
/**
 * Rate limit config schema
 */
export const RateLimitConfigSchema = z.object({
    requests: z.number(),
    windowMs: z.number(),
});
/**
 * App config schema
 */
export const AppConfigSchema = z.object({
    maintenanceMode: z.boolean().optional(),
    maintenanceMessage: z.string().optional(),
    maintenanceEndTime: z.string().optional(),
    registrationEnabled: z.boolean().optional(),
    newBotCreationEnabled: z.boolean().optional(),
    paymentProcessingEnabled: z.boolean().optional(),
    minimumAppVersion: z.string().optional(),
    featureFlags: z.record(z.boolean()).optional(),
    rateLimit: RateLimitConfigSchema.optional(),
    // Billing configuration
    initialFreeMonths: z.number().optional(),
});
/**
 * App config input schema
 */
export const AppConfigInputSchema = z
    .object({
    maintenanceMode: z.boolean().optional(),
    maintenanceMessage: z.string().optional(),
    maintenanceEndTime: z.string().optional(),
    registrationEnabled: z.boolean().optional(),
    newBotCreationEnabled: z.boolean().optional(),
    paymentProcessingEnabled: z.boolean().optional(),
    minimumAppVersion: z.string().optional(),
    featureFlags: z.record(z.boolean()).optional(),
    rateLimit: RateLimitConfigSchema.optional(),
    // Billing configuration
    initialFreeMonths: z.number().optional(),
})
    .strict();
/**
 * Maintenance status schema
 */
export const MaintenanceStatusSchema = z.object({
    inMaintenance: z.boolean(),
    message: z.string().optional(),
    endTime: z.string().optional(),
    minimumAppVersion: z.string().optional(),
});
/**
 * Currency pair schema
 */
export const CurrencyPairSchema = z.object({
    base: z.string(),
    quote: z.string(),
    symbol: z.string(),
    rate: z.number().optional(),
    lastUpdated: z.string().optional(),
});
/**
 * Market data schema
 */
export const MarketDataSchema = z.object({
    currencyPairs: z.array(CurrencyPairSchema),
    lastUpdated: z.string(),
    source: z.string(),
});
// ============================================================================
// Auth Log Schemas
// ============================================================================
/**
 * Auth log status schema
 */
export const AuthLogStatusSchema = z.enum(["success", "failed"]);
/**
 * Auth log origin schema
 */
export const AuthLogOriginSchema = z.enum(["mobile", "website", "unknown"]);
/**
 * Auth log action schema
 */
export const AuthLogActionSchema = z.enum(["login", "password_reset_request", "password_reset"]);
/**
 * Auth log schema
 */
export const AuthLogSchema = z.object({
    id: StrapiIdSchema,
    email: z.string(),
    status: AuthLogStatusSchema,
    action: AuthLogActionSchema.nullable().optional(),
    ipAddress: z.string().nullable().optional(),
    userAgent: z.string().nullable().optional(),
    failureReason: z.string().nullable().optional(),
    origin: AuthLogOriginSchema.nullable().optional(),
    metadata: z.record(z.unknown()).nullable().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    user: z.object({ id: StrapiIdSchema, email: z.string(), username: z.string() }).nullable().optional(),
});
/**
 * Auth log stats schema
 */
export const AuthLogStatsSchema = z.object({
    total: z.number(),
    success: z.number(),
    failed: z.number(),
});
/**
 * Auth log query params schema
 */
export const AuthLogQueryParamsSchema = NestedPaginationParamsSchema.extend({
    status: z.string().optional(),
    email: z.string().optional(),
    ipAddress: z.string().optional(),
    origin: z.string().optional(),
    action: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
});
